/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useEffect } from "react"
import { Box, Button, Chip, Typography, Paper, Grid, Card, CardActionArea, CardContent } from "@mui/material"
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { getDocs, collection, query, where, QuerySnapshot, DocumentData, orderBy, onSnapshot, QueryFieldFilterConstraint } from "firebase/firestore"
import { db } from "../../firebase"
import { DataGrid, gridNumberComparator, GridRowSelectionModel } from "@mui/x-data-grid"
import ShippingDestinationForm, { ShipmentProductSnapshot } from "./lib/ShippingDestinationForm"
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'

interface IPartPrice {
  id: string
  part: string
  priceUnit: 'perPiece' | 'perGram'
  frozenPrice: number
  chilledPrice: number
  taxRate?: 8 | 10
  description: string
}

interface IPartSummary {
  part: string
  count: number
  totalWeight: number
  totalPrice: number
}

const getInitialDateRange = () => {
  const today = dayjs()
  const oneYearAgo = today.subtract(1, 'year')
  return { startDate: oneYearAgo, endDate: today }
}

const getItemWeightInGrams = (item: any): number => {
  return typeof item.weight === 'string'
    ? parseFloat(item.weight.replace(/[^\d.-]/g, '')) || 0
    : (typeof item.weight === 'number' ? item.weight : 0)
}

const ShippingRegistration = () => {
  const [allUnshippedItems, setAllUnshippedItems] = useState<DocumentData[]>([])
  const [selectedPart, setSelectedPart] = useState<string>('')
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([])
  const [openShipping, setOpenShipping] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [partPrices, setPartPrices] = useState<IPartPrice[]>([])
  const [searched, setSearched] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'partPrices'), (snapshot) => {
      const prices = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      } as IPartPrice))
      setPartPrices(prices)
    })
    return unsubscribe
  }, [])

  const calculatePrice = (item: any): number => {
    const partPrice = partPrices.find(p => p.part === item.part)
    if (!partPrice) return 0

    const weightInKg = getItemWeightInGrams(item) / 1000
    const pricePerUnit = (item.storageType === '冷凍' || !item.storageType) ? partPrice.frozenPrice : partPrice.chilledPrice

    if (partPrice.priceUnit === 'perPiece') {
      return Math.round(pricePerUnit)
    } else {
      return Math.round(pricePerUnit * weightInKg)
    }
  }

  // 部位に設定された消費税率を取得する関数（未設定の部位は8%とみなす）
  const getTaxRate = (item: any): number => {
    const partPrice = partPrices.find(p => p.part === item.part)
    return partPrice?.taxRate === 10 ? 10 : 8
  }

  const [dateRange, setDateRange] = useState(() => {
    const savedDateRange = sessionStorage.getItem('shippingRegistrationDateRange')
    if (savedDateRange) {
      const parsed = JSON.parse(savedDateRange)
      return {
        startDate: parsed.startDate ? dayjs(parsed.startDate) : null,
        endDate: parsed.endDate ? dayjs(parsed.endDate) : null
      }
    }
    return getInitialDateRange()
  })

  const handleStartDateChange = (newDate: dayjs.Dayjs | null) => {
    const newDateRange = { ...dateRange, startDate: newDate }
    setDateRange(newDateRange)
    sessionStorage.setItem('shippingRegistrationDateRange', JSON.stringify({
      startDate: newDate?.toISOString(),
      endDate: dateRange.endDate?.toISOString()
    }))
  }

  const handleEndDateChange = (newDate: dayjs.Dayjs | null) => {
    const newDateRange = { ...dateRange, endDate: newDate }
    setDateRange(newDateRange)
    sessionStorage.setItem('shippingRegistrationDateRange', JSON.stringify({
      startDate: dateRange.startDate?.toISOString(),
      endDate: newDate?.toISOString()
    }))
  }

  const columns = [
    {
      field: "No",
      headerName: "No",
      width: 10,
      sortComparator: gridNumberComparator,
    },
    {
      field: "individualNo",
      headerName: "個体識別番号",
      width: 200,
      cellClassName: "name-column--cell",
    },
    {
      field: "productNo",
      headerName: "製品番号",
      width: 200,
      cellClassName: "name-column--cell",
    },
    {
      field: "processingDate",
      headerName: "加工日",
      flex: 1,
    },
    {
      field: "part",
      headerName: "部位",
      flex: 1,
    },
    {
      field: "taxRate",
      headerName: "税率",
      width: 70,
      renderCell: (params: any) => `${getTaxRate(params.row)}%`,
    },
    {
      field: "weight",
      headerName: "重量",
      flex: 1,
    },
    {
      field: "storageType",
      headerName: "温度状態",
      width: 100,
    },
    {
      field: "price",
      headerName: "価格（税込）",
      width: 130,
      renderCell: (params: any) => {
        const price = Math.round(calculatePrice(params.row))
        return price > 0 ? `¥${price.toLocaleString('ja-JP', { maximumFractionDigits: 0 })}` : '-'
      },
    },
    {
      field: "detail",
      headerName: "個体詳細",
      flex: 1,
      renderCell: (params: any) => (
        <Chip label="詳細" color="default" size="small" onClick={() => window.open(`/individuals/${params.row.individualNo}`, '_blank')}></Chip>
      ),
    }
  ]

  const search = () => {
    if (dateRange.startDate && dateRange.endDate && dateRange.startDate.isAfter(dateRange.endDate)) {
      setErrorMessage('開始日は終了日より前の日付を選択してください')
      return
    }

    setErrorMessage('')
    setLoading(true)
    setAllUnshippedItems([])
    setSelectedPart('')
    setSelectionModel([])

    const whereConditions: QueryFieldFilterConstraint[] = []

    if (dateRange.startDate && dateRange.endDate) {
      const startDateStr = dateRange.startDate.format('YYYY/MM/DD')
      const nextDayStr = dateRange.endDate.add(1, 'day').format('YYYY/MM/DD')
      whereConditions.push(
        where('processingDate', '>=', startDateStr),
        where('processingDate', '<', nextDayStr)
      )
    } else if (dateRange.startDate) {
      const startDateStr = dateRange.startDate.format('YYYY/MM/DD')
      whereConditions.push(where('processingDate', '>=', startDateStr))
    } else if (dateRange.endDate) {
      const nextDayStr = dateRange.endDate.add(1, 'day').format('YYYY/MM/DD')
      whereConditions.push(where('processingDate', '<', nextDayStr))
    }

    const searchQuery = query(
      collection(db, 'products'),
      ...whereConditions,
      orderBy("processingDate", "asc")
    )

    getDocs(searchQuery).then((_: QuerySnapshot) => {
      const allItems = _.docs.map((doc: DocumentData) => ({ id: doc.id, ...doc.data() }))

      const uniqueItems = allItems.reduce((acc: any[], current: any) => {
        const existingIndex = acc.findIndex(item => item.productNo === current.productNo)
        if (existingIndex === -1) {
          acc.push(current)
        } else {
          const existingDate = new Date(acc[existingIndex].processingDate.replace(/\//g, '-'))
          const currentDate = new Date(current.processingDate.replace(/\//g, '-'))
          if (currentDate > existingDate) {
            acc[existingIndex] = current
          }
        }
        return acc
      }, [])

      const unshippedItems = uniqueItems.filter((item: any) => !item.isSold)

      setAllUnshippedItems(unshippedItems)
      setSearched(true)
      if (unshippedItems.length === 0) {
        setErrorMessage('該当する未出荷商品がありません')
      }
    }).catch((error) => {
      console.error('検索エラー:', error)
      setErrorMessage('検索中にエラーが発生しました。しばらく時間をおいて再度お試しください。')
    }).finally(() => {
      setLoading(false)
    })
  }

  const partSummaries: IPartSummary[] = useMemo(() => {
    const map = new Map<string, IPartSummary>()
    allUnshippedItems.forEach((item: any) => {
      const partName = item.part || '(未設定)'
      const existing = map.get(partName) ?? { part: partName, count: 0, totalWeight: 0, totalPrice: 0 }
      existing.count += 1
      existing.totalWeight += getItemWeightInGrams(item)
      existing.totalPrice += calculatePrice(item)
      map.set(partName, existing)
    })
    return Array.from(map.values()).sort((a, b) => a.part.localeCompare(b.part, 'ja'))
  }, [allUnshippedItems, partPrices])

  const itemsForSelectedPart = useMemo(() => {
    if (!selectedPart) return []
    const filtered = allUnshippedItems.filter((item: any) => (item.part || '(未設定)') === selectedPart)
    return filtered.map((item: any, i: number) => ({ No: i + 1, ...item }))
  }, [allUnshippedItems, selectedPart])

  const selectedProductIds = useMemo(
    () => selectionModel.map((id) => String(id)),
    [selectionModel]
  )

  const selectedItems = useMemo(() => {
    const selectedSet = new Set(selectedProductIds)
    return itemsForSelectedPart.filter((item) => selectedSet.has(item.id))
  }, [itemsForSelectedPart, selectedProductIds])

  const openShippingForm = () => {
    if (selectedProductIds.length === 0) return
    setOpenShipping(true)
  }

  const closeShippingForm = () => {
    setOpenShipping(false)
  }

  const handleShippingComplete = () => {
    setSelectionModel([])
    search()
  }

  const handlePartClick = (part: string) => {
    setSelectedPart(part)
    setSelectionModel([])
  }

  const handleBackToParts = () => {
    setSelectedPart('')
    setSelectionModel([])
  }

  const selectedWeight = useMemo(() => {
    return selectedItems.reduce((sum, item) => sum + getItemWeightInGrams(item), 0)
  }, [selectedItems])

  const selectedPrice = useMemo(() => {
    if (partPrices.length === 0) return 0
    return Math.round(selectedItems.reduce((sum, item) => sum + calculatePrice(item), 0))
  }, [selectedItems, partPrices])

  const currentPartSummary = useMemo(() => {
    return partSummaries.find((s) => s.part === selectedPart)
  }, [partSummaries, selectedPart])

  return (
    <Box display="contents" justifyItems="center">
      <Box display="flex" justifyContent="center" mt={2}>
        <Typography variant="h4">未出荷リスト</Typography>
      </Box>

      <Box display="flex" justifyContent="center" mt={3}>
        <Box display="flex" alignItems="end" gap={3} flexWrap="wrap">
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>加工日期間フィルター</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
              <Box display="flex" alignItems="center" gap={1}>
                <DatePicker
                  label="開始日"
                  value={dateRange.startDate}
                  onChange={handleStartDateChange}
                  slotProps={{ textField: { size: 'small', sx: { width: '140px' } } }}
                />
                <Typography>～</Typography>
                <DatePicker
                  label="終了日"
                  value={dateRange.endDate}
                  onChange={handleEndDateChange}
                  slotProps={{ textField: { size: 'small', sx: { width: '140px' } } }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
        </Box>
      </Box>

      <Box display="flex-col" m={1} justifyItems="center">
        <Box display="flex" justifyContent="center">
          <Typography variant="subtitle2" color="red">{errorMessage}</Typography>
        </Box>
        <Box display="flex" justifyContent="center" mt={2}>
          <Button variant="contained" onClick={() => search()} disabled={loading}>
            {loading ? '検索中...' : '検索'}
          </Button>
        </Box>
      </Box>

      {searched && !selectedPart && partSummaries.length > 0 && (
        <Box display="flex" flexDirection="column" alignItems="center" mt={3} gap={2} px={2}>
          <Typography variant="h5">未出荷商品がある部位</Typography>
          <Typography variant="body2" color="text.secondary">
            部位をクリックすると商品一覧が表示されます
          </Typography>
          <Box sx={{ width: '100%', maxWidth: '1200px' }}>
            <Grid container spacing={2}>
              {partSummaries.map((summary) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={summary.part}>
                  <Card>
                    <CardActionArea onClick={() => handlePartClick(summary.part)}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>{summary.part}</Typography>
                        <Typography variant="body2" color="primary">
                          件数: <strong>{summary.count}件</strong>
                        </Typography>
                        <Typography variant="body2" color="secondary">
                          総重量: <strong>{(summary.totalWeight / 1000).toFixed(1)}kg</strong>
                        </Typography>
                        <Typography variant="body2" color="success.main">
                          総額: <strong>¥{summary.totalPrice.toLocaleString('ja-JP', { maximumFractionDigits: 0 })}</strong>
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      )}

      {selectedPart && (
        <Box display="flex" flexDirection="column" alignItems="center" mt={3} gap={2} px={2}>
          <Box sx={{ width: '100%', maxWidth: '1200px' }} display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToParts}
            >
              部位一覧に戻る
            </Button>
            <Typography variant="h5">{selectedPart}</Typography>
          </Box>

          <Paper elevation={2} sx={{ padding: 2, minWidth: '400px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={3} flexWrap="wrap">
              <Typography variant="h6" color="primary">
                件数: {itemsForSelectedPart.length}件
              </Typography>
              <Typography variant="h6" color="secondary">
                総重量: {((currentPartSummary?.totalWeight ?? 0) / 1000).toFixed(1)}kg
              </Typography>
              <Typography variant="h6" color="success.main">
                総額: ¥{(currentPartSummary?.totalPrice ?? 0).toLocaleString('ja-JP', { maximumFractionDigits: 0 })}
              </Typography>
            </Box>
            {selectedProductIds.length > 0 && (
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={3} flexWrap="wrap" mt={2} pt={2} sx={{ borderTop: '1px solid #eee' }}>
                <Typography variant="body1" color="primary">
                  選択数: <strong>{selectedProductIds.length}件</strong>
                </Typography>
                <Typography variant="body1" color="primary">
                  選択重量: <strong>{(selectedWeight / 1000).toFixed(1)}kg</strong>
                </Typography>
                <Typography variant="body1" color="primary">
                  選択金額: <strong>¥{selectedPrice.toLocaleString('ja-JP', { maximumFractionDigits: 0 })}</strong>
                </Typography>
              </Box>
            )}
          </Paper>

          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={openShippingForm}
              disabled={selectedProductIds.length === 0}
            >
              選択した商品を出荷済みに登録
            </Button>
          </Box>

          <Box sx={{ width: '100%', maxWidth: '1200px' }}>
            <DataGrid
              rows={itemsForSelectedPart}
              columns={columns}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={(model) => setSelectionModel(model)}
              sx={{ minWidth: '800px', width: '100%' }}
            />
          </Box>
        </Box>
      )}

      <ShippingDestinationForm
        productIds={selectedProductIds}
        productSnapshots={selectedItems.map<ShipmentProductSnapshot>((item: any) => ({
          id: item.id,
          productNo: item.productNo,
          individualNo: item.individualNo,
          part: item.part,
          weight: item.weight,
          storageType: item.storageType,
          quantity: 1,
          price: calculatePrice(item),
          taxRate: getTaxRate(item),
        }))}
        isOpen={openShipping}
        onClose={closeShippingForm}
        onShippingComplete={handleShippingComplete}
      />
    </Box>
  )
}

export default ShippingRegistration
